import * as React from 'react';

import { faBars } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { FormattedMessage, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

import logo from 'src/assets/images/logo_white.png';
import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { administrationMessages } from 'src/features/administration/utils/messages';
import { getLocalUserId, PERMISSION } from 'src/features/auth';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import NavigationSidebarButton from 'src/features/navigation/components/NavigationSidebarButton';
import { PATH } from 'src/features/navigation/utils/constants';
import { pageTitles } from 'src/features/navigation/utils/messages';
import { NavigationSettingsSections } from 'src/features/navigation/utils/types';
import { useListUserMemberships } from 'src/features/profile/api/useListUserMemberships';
import useHasPermissions from 'src/hooks/useHasPermissions';
import useScreenSize from 'src/hooks/useScreenSize';
import { useParking } from 'src/providers/ParkingProvider';
import { SectionBlockSeparator } from 'src/registry/components/blocks/SectionBlock';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItemWithIcon,
    DropdownMenuTrigger,
    DropdownMenuItem,
} from 'src/registry/ui/DropdownMenu';
import { cn } from 'src/utils';
import { ICON_MAP } from 'src/utils/constants';

import type { NavigationLinkDetail } from 'src/features/navigation/utils/types';

const NavigationSidebar = () => {
    const { formatMessage } = useIntl();
    const { screenWidth } = useScreenSize();

    const [visibleNavLinksInMobile, setVisibleNavLinksInMobile] = React.useState<NavigationLinkDetail[]>([]);
    const { parkingId } = useParking();

    const userId = getLocalUserId();
    const { data: userMemberships } = useListUserMemberships({ userId, parking: parkingId });
    const isClient = Boolean(userMemberships && userMemberships[0]?.is_client);

    const accessHomeStats = useHasPermissions([PERMISSION.ACCESS_HOME_STATS]);

    const accessEntitiesPermission = useHasPermissions([PERMISSION.ACCESS_ENTITIES]);
    const accessMembershipsPermission = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIPS]);
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessServiceProviders = useHasPermissions([PERMISSION.ACCESS_SERVICE_PROVIDERS]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessSpaces = useHasPermissions([PERMISSION.ACCESS_SPACES]);
    const accessRoles = useHasPermissions([PERMISSION.MANAGE_ROLES]);
    const accessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);
    const manageSystemSettings = useHasPermissions([PERMISSION.MANAGE_SYSTEM_SETTINGS]);
    const manageSystemComponents = useHasPermissions([PERMISSION.MANAGE_SYSTEM_COMPONENTS]);
    const manageApiKeys = useHasPermissions([PERMISSION.MANAGE_API_KEYS]);
    const accessAccessKeys = useHasPermissions([PERMISSION.ACCESS_ACCESS_KEYS]);
    const accessMembershipTypes = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);
    const accessTroubleshooting = useHasPermissions([PERMISSION.ACCESS_TROUBLESHOOTING]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);
    const accessValidatorAccounts = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);
    const accessAllowLists = useHasPermissions([PERMISSION.ACCESS_ALLOW_LISTS]);

    const accessAdministration = useHasPermissions(
        [
            PERMISSION.ACCESS_SITES,
            PERMISSION.ACCESS_SERVICE_PROVIDERS,
            PERMISSION.ACCESS_ZONES,
            PERMISSION.ACCESS_GATEWAYS,
            PERMISSION.ACCESS_SPACES,
            PERMISSION.ACCESS_ROLES,
            PERMISSION.ACCESS_USERS,
            PERMISSION.MANAGE_SYSTEM_SETTINGS,
            PERMISSION.MANAGE_SYSTEM_COMPONENTS,
            PERMISSION.ACCESS_ACCESS_KEYS,
            PERMISSION.ACCESS_MEMBERSHIP_TYPES,
            PERMISSION.ACCESS_TROUBLESHOOTING,
            PERMISSION.ACCESS_PRICING_PLANS,
            PERMISSION.MANAGE_VALIDATIONS,
        ],
        true,
    );

    const accessValidations = useHasPermissions([PERMISSION.ALLOW_VALIDATIONS]);

    const accessMonitoring = useHasPermissions(
        [
            PERMISSION.ACCESS_PAY_STATIONS_MONITORING,
            PERMISSION.ACCESS_DEVICES_MONITORING,
            PERMISSION.ACCESS_GATEWAYS_MONITORING,
        ],
        true,
    );

    const accessPayments = useHasPermissions(
        [
            PERMISSION.ACCESS_PAYMENTS,
            PERMISSION.ACCESS_PAY_STATIONS,
            PERMISSION.ACCESS_PAY_STATION_OPERATIONS,
            PERMISSION.ACCESS_PAY_STATION_EVENTS,
        ],
        true,
    );

    const accessStatistics = useHasPermissions(
        [PERMISSION.ACCESS_PAYMENT_STATISTICS, PERMISSION.ACCESS_OCCUPANCY_STATISTICS],
        true,
    );

    const accessVisitorsModule = useHasPermissions(
        [
            PERMISSION.ACCESS_ACTIVE_STAYS,
            PERMISSION.ACCESS_PAST_STAYS,
            PERMISSION.ACCESS_VISITORS,
            PERMISSION.ACCESS_GATEWAY_PASSES,
            PERMISSION.ACCESS_EVENT_LOGS,
        ],
        true,
    );

    const navigationLinkDetails: NavigationLinkDetail[] = React.useMemo(() => {
        return [
            {
                link: PATH.HOME,
                icon: ICON_MAP.HOME,
                tooltip: pageTitles.home,
                hasPermission: accessHomeStats || isClient,
            },
            {
                link: PATH.VISITORS,
                icon: ICON_MAP.CLIENT,
                tooltip: pageTitles.visitors,
                hasPermission: accessVisitorsModule,
            },
            {
                link: PATH.MEMBERSHIPS,
                icon: ICON_MAP.SUBSCRIPTION,
                tooltip: pageTitles.memberships,
                hasPermission: accessMembershipsPermission,
            },
            {
                link: PATH.ENTITIES,
                icon: ICON_MAP.USERS,
                tooltip: pageTitles.entitiesCRM,
                hasPermission: accessEntitiesPermission,
            },
            {
                link: PATH.MONITORING,
                icon: ICON_MAP.WAVE_PULSE,
                tooltip: pageTitles.monitoring,
                hasPermission: accessMonitoring,
            },
            {
                link: PATH.PAYMENTS,
                icon: ICON_MAP.CIRCLE_EURO,
                tooltip: pageTitles.payments,
                hasPermission: accessPayments,
            },
            {
                link: PATH.STATISTICS,
                icon: ICON_MAP.PIE_CHART,
                tooltip: pageTitles.statistics,
                hasPermission: accessStatistics,
            },
            {
                link: PATH.VALIDATIONS,
                icon: ICON_MAP.TICKET_PERFORATED,
                tooltip: pageTitles.validation,
                hasPermission: accessValidations,
            },
            {
                link: PATH.PROFILE,
                icon: ICON_MAP.MEMBER,
                tooltip: pageTitles.profile,
                hasPermission: true,
            },
            {
                link: PATH.ADMINISTRATION,
                icon: ICON_MAP.COG,
                tooltip: pageTitles.settings,
                hasPermission: accessAdministration,
            },
        ];
    }, [
        accessHomeStats,
        isClient,
        accessVisitorsModule,
        accessMembershipsPermission,
        accessEntitiesPermission,
        accessMonitoring,
        accessPayments,
        accessStatistics,
        accessValidations,
        accessAdministration,
    ]);

    const navigationSettingsSections: NavigationSettingsSections = React.useMemo(
        () => [
            {
                label: administrationMessages.users,
                hasPermission: accessUsers || accessRoles,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/users`,
                        label: administrationMessages.users,
                        hasPermission: accessUsers,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/roles`,
                        label: administrationMessages.roles,
                        hasPermission: accessRoles,
                    },
                ],
            },
            {
                label: administrationMessages.sites,
                hasPermission:
                    accessServiceProviders ||
                    accessSites ||
                    accessZones ||
                    accessGateways ||
                    accessSpaces ||
                    accessAllowLists,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/service-provider`,
                        label: administrationMessages.organisations,
                        hasPermission: accessServiceProviders,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/site`,
                        label: administrationMessages.sites,
                        hasPermission: accessSites,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/zone`,
                        label: administrationMessages.zones,
                        hasPermission: accessZones,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/gateway`,
                        label: administrationMessages.gateways,
                        hasPermission: accessGateways,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/spaces`,
                        label: administrationMessages.spaces,
                        hasPermission: accessSpaces,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/white-list`,
                        label: administrationMessages.whiteLists,
                        hasPermission: accessAllowLists,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/black-list`,
                        label: administrationMessages.blackLists,
                        hasPermission: accessAllowLists,
                    },
                ],
            },
            {
                label: membershipTypeMessages.memberships,
                hasPermission: accessMembershipTypes,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/membership-templates`,
                        label: membershipTypeMessages.templates,
                        hasPermission: accessMembershipTypes,
                    },
                ],
            },
            {
                label: accessKeyMessages.accessKeys,
                hasPermission: accessAccessKeys,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/access-key`,
                        label: accessKeyMessages.accessKeys,
                        hasPermission: accessAccessKeys,
                    },
                ],
            },
            {
                label: administrationMessages.prices,
                hasPermission: accessPricingPlans,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/pricing-plan`,
                        label: administrationMessages.pricingPlans,
                        hasPermission: accessPricingPlans,
                    },
                ],
            },
            {
                label: administrationMessages.validations,
                hasPermission: accessValidations || accessValidatorAccounts,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/validator-accounts`,
                        label: administrationMessages.validatorAccounts,
                        hasPermission: accessValidatorAccounts,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/validation-types`,
                        label: administrationMessages.validationTypes,
                        hasPermission: accessValidations,
                    },
                ],
            },
            // {
            //     label: administrationMessages.billingSettings,
            //     hasPermission: accessServiceProviders,
            //     links: [
            //         {
            //             link: `/${PATH.ADMINISTRATION}/billing-entity`,
            //             label: billingMessages.billingEntities,
            //             hasPermission: accessServiceProviders,
            //         },
            //     ],
            // },
            {
                label: administrationMessages.troubleshooting,
                hasPermission: accessTroubleshooting,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/actions`,
                        label: administrationMessages.actions,
                        hasPermission: accessTroubleshooting,
                    },
                ],
            },
            {
                label: administrationMessages.systems,
                hasPermission: manageSystemComponents || manageSystemSettings || manageApiKeys,
                links: [
                    {
                        link: `/${PATH.ADMINISTRATION}/system-settings`,
                        label: administrationMessages.settings,
                        hasPermission: manageSystemComponents,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/system-components`,
                        label: administrationMessages.components,
                        hasPermission: manageSystemSettings,
                    },
                    {
                        link: `/${PATH.ADMINISTRATION}/api-keys`,
                        label: administrationMessages.apiKeys,
                        hasPermission: manageApiKeys,
                    },
                ],
            },
        ],
        [
            accessAccessKeys,
            accessAllowLists,
            accessGateways,
            accessMembershipTypes,
            accessPricingPlans,
            accessRoles,
            accessServiceProviders,
            accessSites,
            accessSpaces,
            accessTroubleshooting,
            accessUsers,
            accessValidations,
            accessValidatorAccounts,
            accessZones,
            manageApiKeys,
            manageSystemComponents,
            manageSystemSettings,
        ],
    );

    const navigationLinksWithPermissions: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinkDetails.filter((item) => item.hasPermission);
    }, [navigationLinkDetails]);

    const hamburgerMenuItems: NavigationLinkDetail[] = React.useMemo(() => {
        return navigationLinksWithPermissions
            .filter((item) => !visibleNavLinksInMobile.some((hamburgerItem) => item.link === hamburgerItem.link))
            .filter((item) => item.link !== PATH.PROFILE && item.link !== PATH.ADMINISTRATION);
    }, [visibleNavLinksInMobile, navigationLinksWithPermissions]);

    const listOfVisibleMobileNavLinks: string[] = visibleNavLinksInMobile.map((item) => item.link);

    React.useEffect(() => {
        const isMobile = screenWidth < 640;
        const isLargePhone = screenWidth > 400 && screenWidth < 640;
        const numberOfNavLinksShownOnMobile = isMobile && isLargePhone ? 4 : 3;

        const visibleNavLinks = navigationLinksWithPermissions.slice(
            0,
            isMobile ? numberOfNavLinksShownOnMobile : navigationLinksWithPermissions.length,
        );

        setVisibleNavLinksInMobile(visibleNavLinks.map((item) => item));
    }, [screenWidth, parkingId, navigationLinksWithPermissions]);

    const sliceNumber = accessAdministration && !isClient ? 2 : 1;

    return (
        <aside className="bg-primary-700 fixed top-0 left-0 z-40 flex h-16 w-full flex-col overflow-x-hidden overflow-y-auto sm:h-full sm:w-20">
            <div className="mx-4 my-3 flex size-10 shrink-0 items-center justify-center rounded-md sm:m-4 sm:size-12 sm:bg-white sm:p-2 dark:sm:bg-gray-950">
                <img src={logo} alt="SmartPark" />
            </div>
            <nav className="bg-primary-700 text-primary-300 fixed bottom-0 z-50 flex w-full justify-around p-2 text-xl sm:relative sm:h-full sm:flex-col sm:justify-normal sm:gap-3 sm:px-4 sm:pt-0 sm:pb-4 dark:bg-gray-950">
                {navigationLinksWithPermissions.map((item, index) => {
                    return (
                        <React.Fragment key={item.link}>
                            {index === navigationLinksWithPermissions.length - sliceNumber && (
                                <div className="mt-auto hidden sm:flex" />
                            )}
                            <NavigationSidebarButton
                                className={cn(
                                    item.link !== 'profile' &&
                                        !listOfVisibleMobileNavLinks.includes(item.link) &&
                                        'hidden sm:flex',
                                )}
                                icon={item.icon}
                                link={item.link}
                                tooltip={formatMessage(item.tooltip)}
                            />
                        </React.Fragment>
                    );
                })}

                {hamburgerMenuItems.length > 0 && visibleNavLinksInMobile.length > 0 && (
                    <DropdownMenu modal={false}>
                        <DropdownMenuTrigger className="hover:bg-primary-600 data-[state=open]:bg-primary-600 xs:size-12 flex size-10 shrink-0 items-center justify-center rounded-md transition-colors data-[state=open]:text-white">
                            <FontAwesomeIcon icon={faBars} />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom xs:h-[calc(100vh-7.75rem)] z-30 h-[calc(100vh-7.25rem)] w-svw -translate-x-5 rounded-3xl rounded-b-none border-0 p-6 pb-3 text-black">
                            {hamburgerMenuItems.map((item) => {
                                {
                                    return (
                                        <NavLink to={item.link} key={item.link}>
                                            <DropdownMenuItemWithIcon
                                                variant="sidebar"
                                                iconClassName="text-primary-600"
                                                icon={item.icon}
                                                className="focus:bg-primary-100 text-lg"
                                            >
                                                <FormattedMessage {...item.tooltip} />
                                            </DropdownMenuItemWithIcon>
                                        </NavLink>
                                    );
                                }
                            })}
                            <Collapsible.Root>
                                <Collapsible.Trigger asChild>
                                    <li className="group hover:bg-primary-100 relative flex cursor-pointer items-center rounded-md px-2 py-1.5 pl-8 text-lg outline-hidden transition-colors select-none data-disabled:pointer-events-none data-disabled:opacity-50">
                                        <span className="text-primary-600 absolute left-2 flex size-3.5 items-center justify-center">
                                            <FontAwesomeIcon icon={ICON_MAP.COG} className="opacity-80" />
                                        </span>
                                        <FormattedMessage {...pageTitles.settings} />
                                        <span className="text-primary-600 ml-auto group-data-[state=open]:hidden">
                                            <FontAwesomeIcon icon={ICON_MAP.CHEVRON_DOWN} className="opacity-80" />
                                        </span>
                                        <span className="text-primary-600 ml-auto group-data-[state=closed]:hidden">
                                            <FontAwesomeIcon icon={ICON_MAP.UP} className="opacity-80" />
                                        </span>
                                    </li>
                                </Collapsible.Trigger>
                                <Collapsible.Content className="w-full overflow-hidden pl-8 data-[state=closed]:animate-[slideUp_300ms_ease-out_forwards] data-[state=open]:animate-[slideDown_300ms_ease-out_forwards]">
                                    {navigationSettingsSections
                                        .filter((section) => section.hasPermission)
                                        .map((section, index) => {
                                            return (
                                                <div className="mt-2 flex flex-col" key={index}>
                                                    {index > 0 && <SectionBlockSeparator className="my-2" />}
                                                    <span className="text-lg font-semibold">
                                                        <FormattedMessage {...section.label} />
                                                    </span>
                                                    {section.links
                                                        .filter((link) => link.hasPermission)
                                                        .map((link) => (
                                                            <NavLink to={link.link} key={link.link}>
                                                                <DropdownMenuItem className="text-lg">
                                                                    <FormattedMessage {...link.label} />
                                                                </DropdownMenuItem>
                                                            </NavLink>
                                                        ))}
                                                </div>
                                            );
                                        })}
                                </Collapsible.Content>
                            </Collapsible.Root>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
            </nav>
        </aside>
    );
};

export default NavigationSidebar;
